import { z } from 'zod';
import { paginationQuery } from './common.js';
/* ------------------------------------------------------------------ */
/* Módulo Proyectos (fase 1): obras, presupuesto y costos imputados    */
/* ------------------------------------------------------------------ */
export const PROJECT_STATUSES = [
    { code: 'active', name: 'Activo' },
    { code: 'paused', name: 'En pausa' },
    { code: 'completed', name: 'Terminado' },
    { code: 'cancelled', name: 'Cancelado' },
];
export const projectStatus = z.enum(['active', 'paused', 'completed', 'cancelled']);
/** Base del costo de las facturas imputadas: con IVA (total) o sin el IVA acreditable declarado en Recepción. */
export const PROJECT_COST_BASES = [
    { code: 'total', name: 'Con IVA (total del comprobante)' },
    { code: 'net_of_credit', name: 'Sin el IVA acreditable' },
];
export const projectCostBasis = z.enum(['total', 'net_of_credit']);
/** Sobre qué se calcula el margen de planilla. `payroll` es la base sin sumarle CCSS e INS. */
export const LABOR_MARGIN_BASES = [
    { code: 'payroll', name: 'Sobre la base, sin las cargas' },
    { code: 'with_charges', name: 'Sobre la base más las cargas' },
];
export const laborMarginBase = z.enum(['payroll', 'with_charges']);
/**
 * Base del porcentaje de administración. `cost` sigue la base del costo de las facturas; las otras dos la fijan
 * aunque el costo se cuente distinto.
 */
export const ADMIN_FEE_BASES = [
    { code: 'cost', name: 'La misma del costo de las facturas' },
    { code: 'total', name: 'Siempre con IVA' },
    { code: 'net_of_credit', name: 'Siempre sin el IVA acreditable' },
];
export const adminFeeBasis = z.enum(['cost', 'total', 'net_of_credit']);
/** El cierre al consultar cambia si la obra cambia. Con versiones se puede guardar una copia que no se mueve. */
export const CLOSING_MODES = [
    { code: 'live', name: 'Al consultar, con los datos de ahora' },
    { code: 'snapshot', name: 'Se puede guardar una versión' },
];
export const closingMode = z.enum(['live', 'snapshot']);
/** Quién cotiza. `project` cobra a toda la planilla; `worker` solo a quienes tienen el interruptor. */
export const CHARGES_MODES = [
    { code: 'project', name: 'Toda la planilla del proyecto' },
    { code: 'worker', name: 'Solo quienes llevan cargas' },
];
export const chargesMode = z.enum(['project', 'worker']);
/**
 * `unified` es la fórmula de siempre (un porcentaje de administración y un margen). `groups` reparte la
 * administración por grupo de costo y el margen de planilla sale del grupo Planilla.
 */
export const ADMIN_MODELS = [
    { code: 'unified', name: 'Un solo porcentaje' },
    { code: 'groups', name: 'Por grupos de costo' },
];
export const adminModel = z.enum(['unified', 'groups']);
export const COST_GROUP_ORIGINS = [
    { code: 'compras', name: 'Compras' },
    { code: 'nomina', name: 'Planilla' },
];
const optionalText = (max) => z.string().trim().max(max).nullable().optional();
const pct = z.coerce.number().min(0, 'No puede ser negativo').max(1000, 'Revisá el porcentaje');
const money = z.coerce.number().min(0, 'No puede ser negativo').max(1e13);
export const projectInput = z
    .object({
    code: optionalText(30),
    name: z.string().trim().min(2, 'Requerido').max(160),
    description: optionalText(2000),
    customerId: z.uuid().nullable().optional(),
    status: projectStatus.default('active'),
    startDate: z.iso.date('Fecha inválida').nullable().optional(),
    endDate: z.iso.date('Fecha inválida').nullable().optional(),
    budget: money.default(0),
    adminFeeMode: z.enum(['pct', 'amount']).default('pct'),
    adminFeePct: pct.default(0),
    adminFeeAmount: money.default(0),
    costBasis: projectCostBasis.default('total'),
    /** Porcentaje de administración: sigue el costo, o se fija con IVA / sin el IVA acreditable. */
    adminFeeBasis: adminFeeBasis.default('cost'),
    laborCcssPct: pct.default(26.83),
    laborInsPct: pct.default(3.75),
    laborMarginPct: pct.default(0),
    laborMarginBase: laborMarginBase.default('payroll'),
    /** Dietas y transporte cotizan CCSS e INS (y entran a la base del margen). */
    chargesIncludeAllowances: z.boolean().default(true),
    /** Con el proyecto en Terminado no se registran horas, costos ni recálculos. */
    lockOnComplete: z.boolean().default(false),
    closingMode: closingMode.default('live'),
    chargesMode: chargesMode.default('project'),
    adminModel: adminModel.default('unified'),
    /** Metros cuadrados de la obra, para el costo por m² del cierre. Vacío es null (coerce haría 0). */
    areaSqm: z.preprocess((v) => (v === '' || v == null ? null : v), z.coerce.number().positive('Mayor a cero').max(1e9).nullable()),
    /** Porcentajes por grupo. Solo se guardan si vienen; si no, quedan los de la obra. */
    costGroupRates: z
        .array(z.object({
        code: z.string().trim().min(1).max(40),
        pct: pct,
    }))
        .max(20)
        .optional(),
    notes: optionalText(4000),
})
    .superRefine((v, ctx) => {
    if (v.startDate && v.endDate && v.endDate < v.startDate) {
        ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'Debe ser posterior al inicio' });
    }
});
export const projectListQuery = paginationQuery.extend({
    /** `open` = activos y en pausa. */
    status: z.enum(['open', 'active', 'paused', 'completed', 'cancelled']).optional(),
    customerId: z.uuid().optional(),
});
/* ---------------------------- fases y partidas ---------------------------- */
export const projectPhaseInput = z.object({
    name: z.string().trim().min(2, 'Requerido').max(120),
    budget: money.default(0),
    sortOrder: z.coerce.number().int().min(0).max(10_000).optional(),
});
export const projectAdminItemInput = z
    .object({
    name: z.string().trim().min(2, 'Requerido').max(120),
    kind: z.enum(['amount', 'percent']).default('amount'),
    value: z.coerce.number().min(0, 'No puede ser negativo').max(1e13),
    notes: optionalText(500),
    sortOrder: z.coerce.number().int().min(0).max(10_000).optional(),
})
    .superRefine((v, ctx) => {
    if (v.kind === 'percent' && v.value > 1000) {
        ctx.addIssue({ code: 'custom', path: ['value'], message: 'Revisá el porcentaje' });
    }
});
/* ---------------------------- asignaciones ---------------------------- */
const share = z.coerce.number().gt(0, 'Mayor a cero').max(100, 'Máximo 100 %');
const amountCrc = z.coerce
    .number()
    .refine((n) => n !== 0, 'Distinto de cero')
    .refine((n) => Math.abs(n) <= 1e13, 'Monto inválido');
/**
 * Imputa un gasto (o una línea del comprobante del que nace) a un proyecto. Sin monto ni porcentaje va el 100 %.
 * En una nota de crédito el monto es negativo (el servidor ajusta el signo del porcentaje).
 */
export const projectAllocationInput = z.object({
    expenseId: z.uuid(),
    /** Índice en las líneas del comprobante recibido; null = gasto completo. */
    lineIndex: z.coerce.number().int().min(0).nullable().optional(),
    amountCrc: amountCrc.nullable().optional(),
    sharePct: share.nullable().optional(),
    phaseId: z.uuid().nullable().optional(),
    /** Grupo de costo de la compra. Si no viene, se hereda de la categoría. */
    costGroupId: z.uuid().nullable().optional(),
    notes: optionalText(300),
});
export const projectAllocationUpdateInput = z.object({
    /** Mueve la asignación a otro proyecto. */
    projectId: z.uuid().optional(),
    amountCrc: amountCrc.nullable().optional(),
    sharePct: share.nullable().optional(),
    phaseId: z.uuid().nullable().optional(),
    costGroupId: z.uuid().nullable().optional(),
    notes: optionalText(300),
});
/** Reemplaza todas las asignaciones de un gasto (completa o por líneas a varios proyectos/fases). */
export const expenseAllocationSplitInput = z.object({
    allocations: z
        .array(z.object({
        projectId: z.uuid(),
        phaseId: z.uuid().nullable().optional(),
        lineIndex: z.coerce.number().int().min(0).nullable().optional(),
        amountCrc: amountCrc.nullable().optional(),
        sharePct: share.nullable().optional(),
        costGroupId: z.uuid().nullable().optional(),
        notes: optionalText(300),
    }))
        .max(500, 'Demasiadas asignaciones'),
});
/** Bandeja "Por imputar": asigna lo pendiente de varios gastos a un proyecto (y fase). */
export const projectBulkAllocationInput = z.object({
    expenseIds: z.array(z.uuid()).min(1, 'Elegí al menos un gasto').max(200, 'Máximo 200 por lote'),
    phaseId: z.uuid().nullable().optional(),
    costGroupId: z.uuid().nullable().optional(),
});
/* ---------------------------- consultas del proyecto ---------------------------- */
export const projectCostsQuery = paginationQuery.extend({
    phaseId: z.string().optional(),
    kind: z.enum(['invoice', 'credit', 'expense']).optional(),
});
export const projectItemsQuery = paginationQuery.extend({ phaseId: z.string().optional() });
export const unassignedExpensesQuery = paginationQuery.extend({
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    source: z.enum(['manual', 'reception']).optional(),
    /** `partial` = con algo imputado pero no todo. */
    state: z.enum(['none', 'partial']).optional(),
});
export const projectClosingInput = z.object({
    note: z.string().trim().max(500, 'Máximo 500 caracteres').nullable().optional(),
});
export const projectClosingFileQuery = z.object({
    format: z.enum(['pdf', 'xlsx']).default('pdf'),
});
//# sourceMappingURL=projects.js.map